/*
  Marquoir : tiroir panier et lignes du panier (P2).

  <mq-tiroir-panier> (sections/cart-drawer.liquid)
  - <dialog> modal latéral. S'ouvre sur l'événement mq:panier-ajout (émis sur document après /cart/add.js)
    et au clic sur tout lien vers le panier (délégation sur document : l'en-tête n'a rien à changer).
  - Se ferme par Échap, « Fermer » ou un clic sur le voile. Focus piégé, rendu à l'élément d'origine.
  - Ouverture 250 ms (« pose »), fermeture 200 ms ; aucune animation en mouvement réduit.
  - Sur la page panier, il ne s'ouvre pas : le lien recharge la page, comme sans JavaScript.

  <mq-zone-panier> (tiroir et page panier, sections/main-cart.liquid)
  - Quantités (− / + / saisie, regroupées 400 ms), retrait, vignettes SVG du chiffre (mq/chiffre-svg, mode rendu).
  - Contenu rendu par le serveur, puis remplacé par la Section Rendering API (?sections=…) ou par le paramètre
    sections de /cart/change.js. Les requêtes au panier passent une par une, dans l'ordre.

  Après chaque changement : [data-mq-panier-compte] et événement mq:panier-maj { item_count, source: 'tiroir' }.
  mq:panier-ajout accepte en détail (tout est facultatif) : { sections: { 'cart-drawer': html }, key, items: [{ key }],
  remplacement }. Avec les sections, le tiroir s'ouvre sans requête ; remplacement (« Modifier » depuis la fiche) :
  il annonce « Panier mis à jour. » au lieu de « Ajouté au panier. ».
  Lien « Modifier » : la fiche reçoit &ligne=<clé> et remplace cette ligne (assets/composer.js).
  API : window.Marquoir.panier.ouvrir(), .fermer(), .actualiser().
*/

const MOUVEMENT_REDUIT = window.matchMedia('(prefers-reduced-motion: reduce)');
const POSE = 'cubic-bezier(.2, 0, 0, 1)';
const SORTIE = 'cubic-bezier(.3, 0, 1, 1)';
const DUREE_OUVERTURE = 250;
const DUREE_FERMETURE = 200;
const DUREE_POSE = 300;
const ATTENTE_QUANTITE = 400;
const FOCALISABLES = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const zones = new Set();
let file = Promise.resolve();

function racineBoutique() {
  const racine = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  return racine.endsWith('/') ? racine : `${racine}/`;
}

function cheminDe(url) {
  try {
    return new URL(url, window.location.href).pathname.replace(/\/+$/, '') || '/';
  } catch (e) {
    return '';
  }
}

function visible(element) {
  return (
    element instanceof Element &&
    element.getClientRects().length > 0 &&
    window.getComputedStyle(element).visibility !== 'hidden'
  );
}

// Une requête au panier à la fois, dans l'ordre des gestes (une réponse ancienne n'écrase jamais une récente).
function enFile(tache) {
  const suite = file.then(tache, tache);
  file = suite.catch(() => {});
  return suite;
}

function idsZones() {
  return [...new Set([...zones].map((zone) => zone.sectionId).filter(Boolean))];
}

// Page de rendu des sections : la page panier quand elle est affichée (sa section n'existe que là),
// sinon la racine de la boutique (le tiroir est une section statique : il ne dépend pas de la page courante).
function contexteSections() {
  for (const zone of zones) {
    if (zone.classList.contains('mq-zone-page')) return window.location.pathname;
  }
  return racineBoutique();
}

async function lireSections(ids = idsZones()) {
  if (!ids.length) return {};
  const url = new URL(contexteSections(), window.location.origin);
  url.searchParams.set('sections', ids.join(','));
  const reponse = await fetch(url, { headers: { Accept: 'application/json' }, cache: 'no-store', credentials: 'same-origin' });
  if (!reponse.ok) throw new Error(String(reponse.status));
  return reponse.json();
}

async function changerLigne(cle, quantite) {
  const reponse = await fetch(`${racineBoutique()}cart/change.js`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: JSON.stringify({ id: cle, quantity: quantite, sections: idsZones(), sections_url: contexteSections() }),
  });
  let donnees = null;
  try {
    donnees = await reponse.json();
  } catch (e) {
    donnees = null;
  }
  if (!reponse.ok || !donnees) {
    throw new Error((donnees && (donnees.description || donnees.message)) || '');
  }
  return donnees;
}

function appliquerSections(sections) {
  if (!sections) return;
  zones.forEach((zone) => {
    const html = sections[zone.sectionId];
    if (typeof html === 'string') zone.remplacer(html);
  });
}

function compteActuel() {
  for (const zone of zones) {
    const n = zone.compte;
    if (n !== null) return n;
  }
  return null;
}

function signalerCompte(n) {
  if (!Number.isInteger(n) || n < 0) return;
  document.querySelectorAll('[data-mq-panier-compte]').forEach((element) => {
    if (element.textContent !== String(n)) element.textContent = String(n);
  });
  document.dispatchEvent(new CustomEvent('mq:panier-maj', { detail: { item_count: n, source: 'tiroir' } }));
}

async function rafraichirTout() {
  const sections = await lireSections();
  appliquerSections(sections);
  signalerCompte(compteActuel());
}

// ---------- Outils partagés, chargés à la demande ----------
let outilsSvg = null;
function chargerOutilsSvg() {
  if (!outilsSvg) {
    outilsSvg = import('mq/chiffre-svg')
      .then(async (module) => ({
        rendre: module.rendreChiffreSVG,
        glyphes: await module.chargerGlyphes(window.Marquoir && window.Marquoir.glyphesUrl),
      }))
      .catch(() => {
        outilsSvg = null;
        return null;
      });
  }
  return outilsSvg;
}

// ---------- Zone de lignes (tiroir ou page panier) ----------
class MqZonePanier extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.section || '';
    this.minuteurs = new Map();
    this.dernierContenu = null;
    this.vignettes = Promise.resolve();
    zones.add(this);
    this.addEventListener('click', this.surClic);
    this.addEventListener('change', this.surChangement);
    this.addEventListener('keydown', this.surTouche);
    this.setAttribute('data-mq-pret', '');
    if (!this.hasAttribute('data-differe')) this.rendreVignettes();
  }

  disconnectedCallback() {
    zones.delete(this);
    this.removeEventListener('click', this.surClic);
    this.removeEventListener('change', this.surChangement);
    this.removeEventListener('keydown', this.surTouche);
    this.minuteurs.forEach((minuteur) => window.clearTimeout(minuteur));
    this.minuteurs.clear();
  }

  // Le tiroir diffère les vignettes jusqu'à sa première ouverture.
  preparer() {
    if (this.hasAttribute('data-differe')) this.removeAttribute('data-differe');
    return this.rendreVignettes();
  }

  get compte() {
    const etat = this.querySelector('[data-mq-etat]');
    const n = etat ? parseInt(etat.dataset.itemCount, 10) : NaN;
    return Number.isInteger(n) ? n : null;
  }

  instantane() {
    const carte = new Map();
    this.querySelectorAll('[data-cle]').forEach((ligne) => carte.set(ligne.dataset.cle, Number(ligne.dataset.quantite) || 0));
    return carte;
  }

  // Lignes nouvelles ou dont la quantité a augmenté depuis l'instantané.
  nouvelles(avant) {
    const cles = [];
    this.querySelectorAll('[data-cle]').forEach((ligne) => {
      const quantite = Number(ligne.dataset.quantite) || 0;
      if (!avant.has(ligne.dataset.cle) || avant.get(ligne.dataset.cle) < quantite) cles.push(ligne.dataset.cle);
    });
    return cles;
  }

  ligne(cle) {
    return cle ? this.querySelector(`[data-cle="${CSS.escape(cle)}"]`) : null;
  }

  remplacer(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const source =
      doc.querySelector(`mq-zone-panier[data-section="${CSS.escape(this.sectionId)}"]`) || doc.querySelector('mq-zone-panier');
    if (!source) return false;
    const contenu = source.innerHTML;
    if (contenu === this.dernierContenu) return false;

    const actif = document.activeElement;
    const avaitFocus = actif instanceof HTMLElement && actif !== document.body && this.contains(actif);
    const repere = avaitFocus && actif.closest('[data-mq-focus]');
    const cleFocus = repere ? repere.dataset.mqFocus : '';
    const defilant = this.querySelector('[data-mq-defilement]');
    const haut = defilant ? defilant.scrollTop : 0;

    this.innerHTML = contenu;
    this.dernierContenu = contenu;

    const nouveauDefilant = this.querySelector('[data-mq-defilement]');
    if (nouveauDefilant && haut) nouveauDefilant.scrollTop = haut;
    if (!this.hasAttribute('data-differe')) this.rendreVignettes();

    if (avaitFocus) {
      const cible = (cleFocus && this.querySelector(`[data-mq-focus="${CSS.escape(cleFocus)}"]`)) || this.secoursFocus();
      if (cible) cible.focus({ preventScroll: true });
    }
    return true;
  }

  secoursFocus() {
    const section = this.closest('.shopify-section') || document;
    return this.querySelector('[data-mq-focus-secours]') || section.querySelector('[data-mq-focus-secours]');
  }

  rendreVignettes() {
    const cibles = [...this.querySelectorAll('[data-mq-vignette]:not([data-mq-vignette-rendue])')];
    if (!cibles.length) return this.vignettes;
    this.vignettes = chargerOutilsSvg().then((outils) => {
      if (!outils) return;
      cibles.forEach((element) => {
        if (!element.isConnected) return;
        try {
          const finition = element.dataset.finition;
          const svg = outils.rendre(outils.glyphes, element.dataset.i1, element.dataset.i2, {
            mode: finition ? 'rendu' : 'dessin',
            finition: finition || 'doree',
          });
          svg.classList.add('mq-vignette__svg');
          svg.removeAttribute('width');
          svg.removeAttribute('height');
          element.replaceChildren(svg);
          element.setAttribute('data-mq-vignette-rendue', '');
        } catch (e) {
          // lettre hors A–Z : le repli typographique reste affiché
        }
      });
    });
    return this.vignettes;
  }

  // Animation signature (charte) : la seconde lettre glisse de 8 px et se pose sur la première, en 300 ms.
  // Le délai court depuis l'appel : si la vignette arrive tard (glyphes lents), la pose est sautée.
  poser(cle, delai = 0) {
    if (MOUVEMENT_REDUIT.matches) return;
    const debut = performance.now();
    this.vignettes.then(() => {
      const ecoule = performance.now() - debut;
      if (ecoule > delai + 500) return;
      const ligne = this.ligne(cle);
      const lettre = ligne && ligne.querySelector('.mq-chiffre-svg__lettre--2');
      const svg = lettre && lettre.ownerSVGElement;
      if (!svg || typeof lettre.animate !== 'function') return;
      const boite = svg.viewBox && svg.viewBox.baseVal;
      const rect = svg.getBoundingClientRect();
      const echelle = boite && boite.width && boite.height ? Math.min(rect.width / boite.width, rect.height / boite.height) : 1;
      const ecart = 8 / (echelle || 1);
      lettre.animate(
        [
          { transform: `translateX(${ecart}px)`, opacity: 0 },
          { transform: 'translateX(0)', opacity: 1 },
        ],
        { duration: DUREE_POSE, easing: POSE, delay: Math.max(0, delai - ecoule), fill: 'backwards' },
      );
    });
  }

  annoncer(texte) {
    if (!texte) return;
    const dialogue = this.closest('dialog');
    const region = dialogue && dialogue.querySelector('[data-mq-annonce-panier]');
    if (region) {
      region.textContent = '';
      window.setTimeout(() => {
        region.textContent = texte;
      }, 60);
    } else if (window.Marquoir && typeof window.Marquoir.annoncer === 'function') {
      window.Marquoir.annoncer(texte);
    }
  }

  texteMaj() {
    const etat = this.querySelector('[data-mq-etat]');
    return [this.dataset.annonceMaj, etat && etat.dataset.annonceTotal].filter(Boolean).join(' ');
  }

  surClic = (evt) => {
    const cible = evt.target instanceof Element ? evt.target : null;
    if (!cible) return;
    const pas = cible.closest('[data-mq-qte-pas]');
    if (pas && this.contains(pas)) {
      evt.preventDefault();
      this.ajuster(pas);
      return;
    }
    const retrait = cible.closest('[data-mq-retirer]');
    if (retrait && this.contains(retrait)) {
      evt.preventDefault();
      this.retirer(retrait);
    }
  };

  surChangement = (evt) => {
    const champ = evt.target instanceof Element ? evt.target.closest('[data-mq-qte-champ]') : null;
    if (!champ || !this.contains(champ)) return;
    const ligne = champ.closest('[data-cle]');
    if (!ligne) return;
    const voulue = parseInt(champ.value, 10);
    if (!Number.isInteger(voulue) || voulue < 0) {
      champ.value = ligne.dataset.quantite;
      return;
    }
    this.planifier(ligne, voulue, 0);
  };

  // Entrée dans un champ de quantité : appliquer, sans envoyer le formulaire (qui mènerait au paiement).
  surTouche = (evt) => {
    if (evt.key !== 'Enter') return;
    const champ = evt.target instanceof Element ? evt.target.closest('[data-mq-qte-champ]') : null;
    if (!champ || !this.contains(champ)) return;
    evt.preventDefault();
    champ.dispatchEvent(new Event('change', { bubbles: true }));
  };

  ajuster(bouton) {
    const ligne = bouton.closest('[data-cle]');
    const champ = ligne && ligne.querySelector('[data-mq-qte-champ]');
    if (!champ) return;
    const actuelle = Math.max(0, parseInt(champ.value, 10) || 0);
    const voulue = Math.max(0, actuelle + (Number(bouton.dataset.mqQtePas) || 0));
    if (voulue === actuelle) return;
    champ.value = String(voulue);
    this.planifier(ligne, voulue, ATTENTE_QUANTITE);
  }

  retirer(lien) {
    const ligne = lien.closest('[data-cle]');
    if (!ligne) return;
    this.planifier(ligne, 0, 0);
  }

  planifier(ligne, quantite, attente) {
    const cle = ligne.dataset.cle;
    window.clearTimeout(this.minuteurs.get(cle));
    this.effacerErreur(ligne);
    ligne.setAttribute('aria-busy', 'true');
    const lancer = () => {
      this.minuteurs.delete(cle);
      const enCours = this.ligne(cle);
      if (enCours && quantite === Number(enCours.dataset.quantite)) {
        enCours.removeAttribute('aria-busy');
        return;
      }
      this.envoyer(cle, quantite);
    };
    if (attente > 0) this.minuteurs.set(cle, window.setTimeout(lancer, attente));
    else lancer();
  }

  envoyer(cle, quantite) {
    return enFile(async () => {
      try {
        const donnees = await changerLigne(cle, quantite);
        appliquerSections(donnees.sections);
        if (!donnees.sections || typeof donnees.sections[this.sectionId] !== 'string') await rafraichirTout();
        signalerCompte(Number.isInteger(donnees.item_count) ? donnees.item_count : compteActuel());
        this.annoncer(quantite === 0 ? this.dataset.annonceRetrait : this.texteMaj());
      } catch (e) {
        try {
          await rafraichirTout();
        } catch (e2) {
          // l'état affiché reste celui d'avant
        }
        this.erreur(cle, (e && e.message) || this.dataset.erreur);
      } finally {
        const ligne = this.ligne(cle);
        if (ligne) ligne.removeAttribute('aria-busy');
      }
    });
  }

  effacerErreur(ligne) {
    ligne.querySelectorAll('[data-mq-erreur-ligne]').forEach((element) => element.remove());
  }

  erreur(cle, texte) {
    const message = texte || this.dataset.erreur || '';
    if (!message) return;
    const ligne = this.ligne(cle);
    const corps = ligne && ligne.querySelector('.mq-ligne-panier__corps');
    const bloc = document.createElement('p');
    bloc.className = 'mq-erreur';
    bloc.setAttribute('role', 'alert');
    const signe = document.createElement('span');
    signe.className = 'mq-icone mq-icone--erreur';
    signe.setAttribute('aria-hidden', 'true');
    signe.textContent = '!';
    const phrase = document.createElement('span');
    phrase.textContent = message;
    bloc.append(signe, phrase);
    if (corps) {
      bloc.setAttribute('data-mq-erreur-ligne', '');
      corps.append(bloc);
      return;
    }
    const zone = this.querySelector('[data-mq-erreur-zone]');
    if (zone) {
      zone.replaceChildren(signe, phrase);
      zone.hidden = false;
    }
  }
}

// ---------- Tiroir ----------
class MqTiroirPanier extends HTMLElement {
  connectedCallback() {
    this.dialogue = this.querySelector('dialog');
    this.zone = this.querySelector('mq-zone-panier');
    this.voile = this.querySelector('[data-mq-tiroir-voile]');
    if (!this.dialogue || !this.zone) return;
    this.cheminPanier = cheminDe(this.dataset.urlPanier || `${racineBoutique()}cart`);
    this.surPagePanier = this.hasAttribute('data-page-panier');
    this.perime = false;
    this.ouvreur = null;
    this.animations = [];
    this.enFermeture = false;
    this.lenisArrete = false;

    document.addEventListener('mq:panier-ajout', this.surAjout);
    document.addEventListener('mq:panier-maj', this.surMaj);
    document.addEventListener('click', this.surClicDocument);
    document.addEventListener('shopify:section:select', this.surSelection);
    document.addEventListener('shopify:section:deselect', this.surDeselection);
    window.addEventListener('pageshow', this.surRetour);
    this.dialogue.addEventListener('pointerdown', this.surAppui);
    this.dialogue.addEventListener('click', this.surClicDialogue);
    this.dialogue.addEventListener('keydown', this.surClavier);
    this.dialogue.addEventListener('cancel', this.surAnnulation);
    this.dialogue.addEventListener('close', this.surFermeture);

    window.Marquoir = window.Marquoir || {};
    window.Marquoir.panier = {
      ouvrir: (options) => this.ouvrir(options),
      fermer: () => this.fermer(),
      actualiser: () => this.actualiser(),
    };
  }

  disconnectedCallback() {
    document.removeEventListener('mq:panier-ajout', this.surAjout);
    document.removeEventListener('mq:panier-maj', this.surMaj);
    document.removeEventListener('click', this.surClicDocument);
    document.removeEventListener('shopify:section:select', this.surSelection);
    document.removeEventListener('shopify:section:deselect', this.surDeselection);
    window.removeEventListener('pageshow', this.surRetour);
    this.arreterAnimations();
    if (this.dialogue && this.dialogue.open) this.dialogue.close();
    this.deverrouiller();
  }

  estLienPanier(lien) {
    let url;
    try {
      url = new URL(lien.href, window.location.href);
    } catch (e) {
      return false;
    }
    if (url.origin !== window.location.origin) return false;
    const chemin = url.pathname.replace(/\/+$/, '') || '/';
    return chemin === this.cheminPanier || chemin === '/cart';
  }

  surClicDocument = (evt) => {
    if (evt.defaultPrevented || evt.button !== 0 || evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey) return;
    if (this.surPagePanier) return;
    const lien = evt.target instanceof Element ? evt.target.closest('a[href]') : null;
    if (!lien || this.dialogue.contains(lien)) return;
    if ((lien.target && lien.target !== '_self') || lien.hasAttribute('download')) return;
    if (!this.estLienPanier(lien)) return;
    evt.preventDefault();
    this.ouvrir({ declencheur: lien });
  };

  surAjout = async (evt) => {
    const detail = (evt && evt.detail) || {};
    const avant = this.zone.instantane();
    let sections = detail.sections && typeof detail.sections[this.zone.sectionId] === 'string' ? detail.sections : null;
    let echec = false;
    await enFile(async () => {
      try {
        if (!sections) sections = await lireSections();
        appliquerSections(sections);
        this.perime = false;
        signalerCompte(compteActuel());
      } catch (e) {
        echec = true;
      }
    });
    if (this.surPagePanier) return;

    let cles = [];
    if (typeof detail.key === 'string') cles = [detail.key];
    else if (Array.isArray(detail.items)) cles = detail.items.map((item) => item && item.key).filter(Boolean);
    if (!cles.length) cles = this.zone.nouvelles(avant);

    this.ouvrir({ actualiser: false });
    if (echec) this.zone.erreur('', this.zone.dataset.erreur);
    cles.slice(0, 1).forEach((cle) => this.zone.poser(cle, DUREE_OUVERTURE - 40));
    const etat = this.zone.querySelector('[data-mq-etat]');
    const message = detail.remplacement ? this.zone.dataset.annonceMaj : this.dataset.annonceAjout;
    this.zone.annoncer([message, etat && etat.dataset.annonceTotal].filter(Boolean).join(' '));
  };

  surMaj = (evt) => {
    if (evt.detail && evt.detail.source === 'tiroir') return;
    this.perime = true;
    if (this.dialogue.open) this.actualiser();
  };

  surRetour = (evt) => {
    // Retour par l'historique (cache de page) : le panier a pu changer ailleurs.
    if (evt.persisted) this.actualiser();
  };

  surSelection = (evt) => {
    if (evt.target instanceof Node && (evt.target.contains(this) || this.contains(evt.target))) this.ouvrir();
  };

  surDeselection = (evt) => {
    if (evt.target instanceof Node && (evt.target.contains(this) || this.contains(evt.target))) this.fermer();
  };

  actualiser() {
    return enFile(async () => {
      try {
        await rafraichirTout();
        this.perime = false;
      } catch (e) {
        // le contenu affiché reste ; il sera relu à la prochaine ouverture
        this.perime = true;
      }
    });
  }

  ouvrir({ declencheur = null, actualiser = true } = {}) {
    if (this.dialogue.open && !this.enFermeture) return;
    if (this.enFermeture) {
      this.arreterAnimations();
      this.enFermeture = false;
    } else {
      const actif = document.activeElement;
      this.ouvreur = declencheur || (actif instanceof HTMLElement && actif !== document.body ? actif : null);
      this.verrouiller();
      try {
        this.dialogue.showModal();
      } catch (e) {
        this.deverrouiller();
        window.location.assign(this.dataset.urlPanier || `${racineBoutique()}cart`);
        return;
      }
    }
    this.zone.preparer();
    const fermer = this.zone.querySelector('[data-mq-tiroir-fermer]');
    (fermer || this.zone.secoursFocus())?.focus({ preventScroll: true });
    this.animerOuverture();
    if (actualiser) this.actualiser();
  }

  fermer() {
    if (!this.dialogue.open || this.enFermeture) return;
    if (MOUVEMENT_REDUIT.matches || typeof this.zone.animate !== 'function') {
      this.dialogue.close();
      return;
    }
    this.arreterAnimations();
    this.enFermeture = true;
    const panneau = this.zone.animate([{ transform: 'translateX(0)' }, { transform: 'translateX(100%)' }], {
      duration: DUREE_FERMETURE,
      easing: SORTIE,
      fill: 'forwards',
    });
    const voile = this.voile
      ? this.voile.animate([{ opacity: 1 }, { opacity: 0 }], { duration: DUREE_FERMETURE, easing: SORTIE, fill: 'forwards' })
      : null;
    this.animations = [panneau, voile].filter(Boolean);
    panneau.finished
      .then(() => {
        if (!this.enFermeture) return;
        this.enFermeture = false;
        this.dialogue.close();
        this.arreterAnimations();
      })
      .catch(() => {});
  }

  animerOuverture() {
    if (MOUVEMENT_REDUIT.matches || typeof this.zone.animate !== 'function') return;
    this.arreterAnimations();
    const panneau = this.zone.animate([{ transform: 'translateX(100%)' }, { transform: 'translateX(0)' }], {
      duration: DUREE_OUVERTURE,
      easing: POSE,
    });
    const voile = this.voile
      ? this.voile.animate([{ opacity: 0 }, { opacity: 1 }], { duration: DUREE_OUVERTURE, easing: POSE })
      : null;
    this.animations = [panneau, voile].filter(Boolean);
  }

  arreterAnimations() {
    this.animations.forEach((animation) => animation.cancel());
    this.animations = [];
  }

  verrouiller() {
    const racine = document.documentElement;
    const barre = Math.max(0, window.innerWidth - racine.clientWidth);
    racine.style.setProperty('--mq-barre-defilement', `${barre}px`);
    racine.classList.add('mq-tiroir-ouvert');
    const motion = window.Marquoir && window.Marquoir.motion;
    const lenis = motion && motion.lenis;
    if (lenis && typeof lenis.stop === 'function' && !lenis.isStopped) {
      lenis.stop();
      this.lenisArrete = true;
    }
  }

  deverrouiller() {
    const racine = document.documentElement;
    racine.classList.remove('mq-tiroir-ouvert');
    racine.style.removeProperty('--mq-barre-defilement');
    if (this.lenisArrete) {
      const motion = window.Marquoir && window.Marquoir.motion;
      const lenis = motion && motion.lenis;
      if (lenis && typeof lenis.start === 'function') lenis.start();
      this.lenisArrete = false;
    }
  }

  surFermeture = () => {
    this.enFermeture = false;
    this.arreterAnimations();
    this.deverrouiller();
    const origine = this.ouvreur;
    this.ouvreur = null;
    window.requestAnimationFrame(() => {
      const actif = document.activeElement;
      if (actif instanceof HTMLElement && actif !== document.body && visible(actif) && !this.dialogue.contains(actif)) return;
      if (origine && origine.isConnected && visible(origine)) {
        origine.focus({ preventScroll: true });
        return;
      }
      // L'origine a disparu (menu mobile refermé…) : le premier lien « Panier » visible.
      const lien = [...document.querySelectorAll('a[href]')].find(
        (element) => !this.dialogue.contains(element) && this.estLienPanier(element) && visible(element),
      );
      if (lien) lien.focus({ preventScroll: true });
    });
  };

  surAnnulation = (evt) => {
    // Échap passe par surClavier ; ceci couvre les autres demandes de fermeture (geste retour d'Android…).
    if (evt.cancelable) evt.preventDefault();
    this.fermer();
  };

  surAppui = (evt) => {
    this.appuiSurVoile = evt.target === this.voile || evt.target === this.dialogue;
  };

  surClicDialogue = (evt) => {
    const cible = evt.target instanceof Element ? evt.target : null;
    if (!cible) return;
    if (cible.closest('[data-mq-tiroir-fermer]')) {
      evt.preventDefault();
      this.fermer();
      return;
    }
    if ((cible === this.voile || cible === this.dialogue) && this.appuiSurVoile) this.fermer();
    this.appuiSurVoile = false;
  };

  surClavier = (evt) => {
    if (evt.key === 'Escape' || evt.key === 'Esc') {
      evt.preventDefault();
      this.fermer();
      return;
    }
    if (evt.key !== 'Tab') return;
    const elements = [...this.dialogue.querySelectorAll(FOCALISABLES)].filter(visible);
    if (!elements.length) {
      evt.preventDefault();
      return;
    }
    const premier = elements[0];
    const dernier = elements[elements.length - 1];
    const actif = document.activeElement;
    if (evt.shiftKey && (actif === premier || !elements.includes(actif))) {
      evt.preventDefault();
      dernier.focus();
    } else if (!evt.shiftKey && actif === dernier) {
      evt.preventDefault();
      premier.focus();
    }
  };
}

if (!customElements.get('mq-zone-panier')) customElements.define('mq-zone-panier', MqZonePanier);
if (!customElements.get('mq-tiroir-panier')) customElements.define('mq-tiroir-panier', MqTiroirPanier);
